import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Lightning, ArrowLeft, Check, Coins, Infinity as InfinityIcon, CircleNotch, Star,
} from "@phosphor-icons/react";

export default function Billing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);

  useEffect(() => {
    api.get("/api/payments/plans").then((r) => setData(r.data)).catch(() => toast.error("Failed to load plans"));
  }, []);

  const checkout = async (lookup_key) => {
    setLoadingKey(lookup_key);
    try {
      const res = await api.post("/api/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoadingKey(null);
    }
  };

  const total = (user?.credits || 0) + (user?.topup_credits || 0);
  const currentPlan = user?.plan || "free";

  return (
    <div className="min-h-screen bg-forge-bg text-white relative noise-overlay">
      <header className="relative z-10 border-b border-forge-border bg-forge-bg">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="billing-back" onClick={() => navigate("/")} className="text-neutral-400 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </button>
            <Lightning size={22} weight="fill" className="text-forge-primary" />
            <span className="font-mono text-lg tracking-tight">Matrix</span>
          </div>
          <div className="flex items-center gap-1.5 border border-forge-border px-3 py-1.5 rounded-sm" data-testid="billing-balance">
            {user?.unlimited ? (
              <><InfinityIcon size={16} weight="bold" className="text-forge-primary" /><span className="text-sm font-mono text-forge-primary">Unlimited</span></>
            ) : (
              <><Coins size={15} weight="fill" className="text-forge-primary" /><span className="text-sm font-mono">{total} credits</span></>
            )}
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 sm:px-8 py-14">
        <p className="uppercase text-xs tracking-[0.2em] text-forge-primary mb-3">Plans & credits</p>
        <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-3">Fuel your builds</h1>
        <p className="text-neutral-400 text-sm max-w-xl mb-12">
          Every AI build consumes credits. A full app build costs {data?.costs?.build ?? 20} credits, iterations cost {data?.costs?.iteration ?? 5}. Credits reset monthly; top-ups never expire.
        </p>

        {!data ? (
          <div className="flex items-center gap-2 text-neutral-500 text-sm"><CircleNotch size={18} className="animate-spin text-forge-primary" /> Loading plans...</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
              {/* Free */}
              <div className={`border rounded-sm p-6 flex flex-col ${currentPlan === "free" ? "border-forge-primary" : "border-forge-border"} bg-forge-surface`}>
                <h3 className="text-lg font-medium mb-1">Free</h3>
                <div className="flex items-baseline gap-1 mb-5"><span className="text-3xl font-light">$0</span><span className="text-neutral-500 text-sm">/mo</span></div>
                <ul className="space-y-2 text-sm text-neutral-300 flex-1">
                  <li className="flex gap-2"><Check size={16} className="text-forge-primary shrink-0 mt-0.5" /> {data.free_credits} credits / month</li>
                  <li className="flex gap-2"><Check size={16} className="text-forge-primary shrink-0 mt-0.5" /> Live preview & editor</li>
                </ul>
                <div className="mt-6 text-center text-xs uppercase tracking-[0.15em] text-neutral-500 py-2 border border-forge-border rounded-sm">
                  {currentPlan === "free" ? "Current plan" : "Starter"}
                </div>
              </div>

              {/* Paid plans */}
              {data.plans.map((p) => {
                const isPro = p.lookup_key === "pro_monthly";
                const isCurrent = currentPlan === p.plan;
                return (
                  <div key={p.lookup_key} className={`relative border rounded-sm p-6 flex flex-col ${isPro ? "border-forge-primary" : "border-forge-border"} bg-forge-surface`}>
                    {isPro && (
                      <span className="absolute -top-2.5 left-6 bg-forge-primary text-black text-[10px] uppercase tracking-[0.15em] font-medium px-2 py-0.5 flex items-center gap-1">
                        <Star size={10} weight="fill" /> Most power
                      </span>
                    )}
                    <h3 className="text-lg font-medium mb-1">{p.name}</h3>
                    <div className="flex items-baseline gap-1 mb-5"><span className="text-3xl font-light">${p.price}</span><span className="text-neutral-500 text-sm">/mo</span></div>
                    <ul className="space-y-2 text-sm text-neutral-300 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex gap-2"><Check size={16} className="text-forge-primary shrink-0 mt-0.5" /> {f}</li>
                      ))}
                    </ul>
                    <button
                      data-testid={`subscribe-${p.lookup_key}`}
                      onClick={() => checkout(p.lookup_key)}
                      disabled={loadingKey === p.lookup_key || isCurrent || user?.unlimited}
                      className={`mt-6 py-2.5 rounded-sm text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50 ${isPro ? "bg-forge-primary text-black hover:bg-yellow-400" : "border border-forge-primary text-forge-primary hover:bg-forge-primary hover:text-black"}`}
                    >
                      {loadingKey === p.lookup_key ? <CircleNotch size={15} className="animate-spin" /> : null}
                      {isCurrent ? "Current plan" : `Subscribe to ${p.name}`}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Top-ups */}
            <div className="flex items-center gap-2 mb-5">
              <Coins size={20} weight="fill" className="text-forge-primary" />
              <h2 className="text-lg font-medium tracking-tight">Credit top-ups</h2>
              <span className="text-xs text-neutral-500 font-mono">never expire</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {(data.topups || []).map((t) => (
                <div key={t.lookup_key} className="border border-forge-border bg-forge-surface rounded-sm p-5 flex flex-col items-start">
                  <div className="w-10 h-10 flex items-center justify-center bg-forge-primary/10 border border-forge-primary/30 rounded-sm mb-4">
                    <Coins size={18} weight="fill" className="text-forge-primary" />
                  </div>
                  <div className="text-2xl font-light mb-0.5">{t.credits.toLocaleString()}</div>
                  <div className="text-xs text-neutral-500 mb-4">credits · ${t.price}</div>
                  <button
                    data-testid={`buy-${t.lookup_key}`}
                    onClick={() => checkout(t.lookup_key)}
                    disabled={loadingKey === t.lookup_key || user?.unlimited}
                    className="mt-auto w-full bg-forge-primary text-black text-sm font-medium px-3 py-2 rounded-sm hover:bg-yellow-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loadingKey === t.lookup_key ? <CircleNotch size={14} className="animate-spin" /> : null}
                    Buy
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
